import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { deepEqual } from 'fast-equals';
import moment from 'moment-timezone';
import { combineLatest, startWith } from 'rxjs';

import { DateRangeValueType } from '@core/date-range-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { DatePickerComponent } from '@core/time-ui';
import type { DatePickerDirection } from '@core/time-ui';
import {
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownWidth,
  InputComponent,
  OverlayOriginDirective,
  OverlayPosition,
  Select2Component,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import type { DatePickerValue, RelativeUnitOption } from './types';
import { RelativeUnit } from './types';

export const viewsInputFormatDate = (dates: string | string[]) => {
  const datesArr = Array.isArray(dates) ? dates : [dates];
  return datesArr.map(v => moment(v).format('DD MMM YYYY')).join(' - ');
};

@Component({
  selector: 'rt-views-date-range-picker',
  templateUrl: 'date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConnectedOverlayDirective,
    DatePickerComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    InputComponent,
    OverlayOriginDirective,
    TranslatePipe,
    ReactiveFormsModule,
    Select2Component,
    NgTemplateOutlet,
  ],
})
export class DateRangePickerComponent implements OnChanges, OnInit {
  private readonly i18n = inject(I18nService);
  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly DropdownWidth = DropdownWidth;
  public readonly DateRangeValueType = DateRangeValueType;
  public readonly CALENDAR_OPTIONS: Partial<Record<DateRangeValueType, true>> = {
    [DateRangeValueType.Exact]: true,
    [DateRangeValueType.Range]: true,
  };
  public readonly RELATIVE_CUSTOM_OPTIONS: Partial<Record<DateRangeValueType, true>> = {
    [DateRangeValueType.WithinLast]: true,
    [DateRangeValueType.WithinNext]: true,
  };
  public readonly CalendarOptionsPlaceholders: Partial<Record<DateRangeValueType, string>> = {
    [DateRangeValueType.Exact]: 'app.views.filters.absolute_date_placeholder',
    [DateRangeValueType.Range]: 'app.views.filters.range_date_placeholder',
  };

  @Input() public dropdownPosition: OverlayPosition = OverlayPosition.BottomLeft;

  @Input() public value: DatePickerValue | null;
  @Input() public hasTrigger = true;

  @Input() public placeholder: string = this.i18n.translate('app.ui.date_range_picker.placeholder');
  @Input() public label: string = this.i18n.translate('app.common.custom_date');

  @Input() public customDropdownWidth: number = 350;

  @Input()
  public relativeCustomUnits: RelativeUnitOption[] = [
    {
      label: this.i18n.translate('app.views.filters.date_relative_unit.days'),
      value: RelativeUnit.Days,
    },
    {
      label: this.i18n.translate('app.views.filters.date_relative_unit.months'),
      value: RelativeUnit.Months,
    },
    {
      label: this.i18n.translate('app.views.filters.date_relative_unit.quarters'),
      value: RelativeUnit.Quarters,
    },
    {
      label: this.i18n.translate('app.views.filters.date_relative_unit.years'),
      value: RelativeUnit.Years,
    },
  ];

  @Input() public tabIndex: number;
  @Input() public blackout: ((date: moment.Moment) => boolean) | null = null;
  @Input() public dateRangeOrigin: OverlayOriginDirective;

  @Input() public customRangeDirection: DatePickerDirection = 'today-past';

  public valueChange = output<DatePickerValue | null>();

  public datePickerValue: string | string[] | null;
  public relativeCustomUnitControl = new FormControl<`${RelativeUnit}` | null>(null);
  public relativeCustomValueControl = new FormControl<number | null>(null);
  public selectedAbsoluteDateLabel: string | null;
  public valueType: DateRangeValueType | null = null;
  public customDatePickerConfig: Pick<DatePickerComponent, 'mode' | 'months'>;

  @ViewChild('absolutePickerOverlay', { static: true })
  private absolutePickerOverlay: ConnectedOverlayDirective;

  private untilDestroyed = injectUntilDestroyed();

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.onValueChanges(changes.value.currentValue, changes.value.previousValue);
      this.customDatePickerConfig =
        this.valueType === DateRangeValueType.Exact
          ? { mode: 'single', months: 1 }
          : { mode: 'range', months: 2 };
    }
  }

  public ngOnInit() {
    combineLatest([
      this.relativeCustomValueControl.valueChanges.pipe(
        startWith(this.relativeCustomValueControl.value),
      ),
      this.relativeCustomUnitControl.valueChanges.pipe(
        startWith(this.relativeCustomUnitControl.value),
      ),
    ])
      .pipe(this.untilDestroyed)
      .subscribe(([value, unit]) => {
        if (!this.isCustomRelativeType(this.valueType) || value === null || !unit) return;

        this.valueChange.emit({
          type: this.valueType,
          value,
          unit,
        });
      });
  }

  public openAbsolutePicker(absoluteSplitPickerOrigin: OverlayOriginDirective) {
    if (this.valueType === 'exact') {
      this.absolutePickerOverlay.origin = absoluteSplitPickerOrigin;
    } else if (this.valueType === 'range') {
      this.absolutePickerOverlay.origin = this.dateRangeOrigin;
    } else {
      return;
    }
    this.absolutePickerOverlay.recreateOverlay();
    this.absolutePickerOverlay.open = true;
  }

  public handleCustomDateRangeChange(dates: string | string[]): void {
    if (DateRangeValueType.Range === this.valueType && dates[0] && dates[1]) {
      this.selectedAbsoluteDateLabel = viewsInputFormatDate(dates);
      this.datePickerValue = dates;
      this.valueChange.emit({ type: this.valueType, min: dates[0], max: dates[1] });
      this.absolutePickerOverlay.open = false;
    } else if (DateRangeValueType.Exact === this.valueType && dates) {
      this.selectedAbsoluteDateLabel = viewsInputFormatDate(dates);
      this.datePickerValue = dates;
      this.valueChange.emit({ type: this.valueType, date: <string>dates! });
      this.absolutePickerOverlay.open = false;
    }

    this.cdRef.detectChanges();
  }

  private getDatePickerValue(value: DatePickerValue | null): string[] | string | null {
    if (!value) return null;

    if (value.type === DateRangeValueType.Range) {
      return [value.min, value.max].filter(Boolean);
    } else if (value.type === DateRangeValueType.Exact) {
      return value.date;
    } else {
      return null;
    }
  }

  private onValueChanges(
    currentValue: DatePickerValue | null,
    previousValue: DatePickerValue | null,
  ) {
    if (deepEqual(currentValue, previousValue)) return;

    this.valueType = currentValue?.type ?? null;
    this.datePickerValue = this.getDatePickerValue(currentValue);
    this.selectedAbsoluteDateLabel = this.datePickerValue
      ? viewsInputFormatDate(this.datePickerValue)
      : null;

    if (
      currentValue &&
      (currentValue.type === 'within_last' || currentValue.type === 'within_next')
    ) {
      const { value, unit } = currentValue;
      this.relativeCustomUnitControl.setValue(unit);
      this.relativeCustomValueControl.setValue(value);
    }
  }

  private isCustomRelativeType(
    type: DateRangeValueType | null,
  ): type is DateRangeValueType.WithinNext | DateRangeValueType.WithinLast {
    return !!type && !!this.RELATIVE_CUSTOM_OPTIONS[type];
  }
}
